import React, { useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useData } from '../../context/DataContext';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiAlertTriangle, FiCheckCircle, FiSend, FiPackage, FiLoader, FiRotateCcw, FiUser } = FiIcons;

const UnlinkedGarmentsList = () => {
  const { inventoryData, currentProject, manageUnlinkedGarment } = useData();
  const [processingId, setProcessingId] = useState(null);

  const isManager = currentProject?.role === 'manager';

  const unlinkedItems = useMemo(() => {
    const list = [];
    inventoryData.forEach(emp => {
      if (!emp.garments) return;
      emp.garments.forEach(garment => {
        const metadata = garment.metadata || {};
        const status = metadata.GarmentStatus || '';
        const barcode = String(garment.barcode || '');
        const unlinkedStatus = metadata.unlinked_status; // 'withdrawn' | undefined

        // 1. FILTRADO ESTRICTO: Tipología 9 y Estado Unlinked
        if (!barcode.startsWith('9') || !status.toLowerCase().includes('unlinked')) {
          return;
        }

        // 2. REGLA DE VISIBILIDAD POR ROL
        // Si ya está retirada ('withdrawn'):
        // - El Operario (Worker) NO la ve (su trabajo terminó).
        // - El Responsable (Manager) LA VE (para enviarla a fábrica o rectificar).
        if (unlinkedStatus === 'withdrawn' && !isManager) {
          return;
        }

        list.push({
          ...garment,
          ownerName: emp.owner,
          empCode: emp.employeeCode,
          wardrobeName: emp.wardrobeName,
          wardrobeOrder: emp.wardrobeOrder || 9999,
          locker: emp.locker,
          uniqueKey: garment.barcode || Math.random().toString(),
          isWithdrawn: unlinkedStatus === 'withdrawn'
        });
      });
    });

    // Ordenación: Vestuario -> Taquilla
    return list.sort((a, b) => {
      if (a.wardrobeOrder !== b.wardrobeOrder) return a.wardrobeOrder - b.wardrobeOrder;
      return new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' })
        .compare(String(a.locker || ''), String(b.locker || ''));
    });
  }, [inventoryData, isManager]);

  const handleAction = async (barcode, action) => {
    if (!barcode || processingId) return;
    setProcessingId(barcode);
    try {
      await manageUnlinkedGarment(barcode, action);
    } catch (error) {
      console.error('Error in unlinked action:', error);
      alert('Error al procesar la acción');
    } finally {
      setProcessingId(null);
    }
  };

  if (unlinkedItems.length === 0) {
    return (
      <motion.div 
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-3xl border border-slate-100 p-12 text-center shadow-sm"
      >
        <div className="bg-green-50 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 border border-green-100">
          <SafeIcon icon={FiCheckCircle} className="text-4xl text-green-500" />
        </div>
        <h3 className="text-xl font-black text-slate-800 tracking-tight">Todo al día</h3>
        <p className="text-slate-500 mt-2 text-sm font-medium">
          {isManager 
            ? "No hay prendas Tipo 9 pendientes de gestión." 
            : "No tienes prendas pendientes de retirar del vestuario."}
        </p>
      </motion.div>
    );
  }

  return (
    <div className="space-y-4 pb-24">
      {/* Banner de Estado Crítico */}
      <div className="flex items-center justify-between bg-red-600 p-4 rounded-2xl shadow-lg shadow-red-100 border border-red-700">
        <div className="flex items-center gap-3">
          <div className="bg-white/20 p-2 rounded-xl backdrop-blur-sm">
            <SafeIcon icon={FiAlertTriangle} className="text-white text-xl" />
          </div>
          <div>
            <h3 className="font-black text-white text-sm uppercase tracking-wider">Acción Requerida</h3>
            <p className="text-red-100 text-xs font-bold">
              {unlinkedItems.length} prendas Tipología 9 detectadas.
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4">
        <AnimatePresence mode="popLayout">
          {unlinkedItems.map((item) => (
            <motion.div
              key={item.uniqueKey}
              layout
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9, x: -20 }}
              className={`
                rounded-3xl p-5 shadow-sm relative overflow-hidden border-2 transition-all
                ${item.isWithdrawn 
                  ? 'bg-orange-50 border-orange-200' 
                  : 'bg-red-50 border-red-200'
                }
              `}
            >
              {/* Loader de Acción */}
              {processingId === item.barcode && (
                <div className="absolute inset-0 bg-white/90 z-20 flex flex-col items-center justify-center backdrop-blur-[2px]">
                  <SafeIcon icon={FiLoader} className="animate-spin text-red-600 text-3xl" />
                  <span className="text-[10px] font-black text-red-600 mt-2 uppercase tracking-widest">Sincronizando...</span>
                </div>
              )}

              {/* 1. CABECERA: Prenda y Talla */}
              <div className="flex justify-between items-start mb-3">
                <div className="flex-1 min-w-0 pr-2">
                  <h4 className="font-black text-red-950 text-base uppercase tracking-tight leading-tight truncate">
                    {item.garment || 'Artículo sin nombre'}
                  </h4>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className="font-bold text-slate-700 text-sm uppercase truncate">
                      {item.ownerName}
                    </span>
                    <span className="font-black text-red-600 text-sm">
                      ({item.empCode})
                    </span>
                  </div>
                </div>
                {item.size && (
                  <div className="w-10 h-10 rounded-2xl bg-red-600 text-white text-sm font-black flex items-center justify-center shadow-lg shadow-red-200 border-2 border-white shrink-0">
                    {item.size}
                  </div>
                )}
              </div>

              {/* 2. UBICACIÓN: Vestuario y Taquilla */}
              <div className="flex gap-3 mb-4">
                <div className="bg-white/60 border border-red-100 px-3 py-1.5 rounded-xl text-[11px] font-black text-slate-700 uppercase">
                  <span className="text-slate-400 mr-1">Vest:</span> {item.wardrobeName}
                </div>
                <div className="bg-white/60 border border-red-100 px-3 py-1.5 rounded-xl text-[11px] font-black text-slate-700 uppercase">
                  <span className="text-slate-400 mr-1">Taq:</span> {item.locker}
                </div>
              </div>

              {/* 3. CAJA TÉCNICA: Códigos de Identificación */}
              <div className="bg-white/80 rounded-2xl p-3 space-y-2 mb-5 border border-red-100/50">
                <div className="flex items-center justify-between text-xs px-1">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">Barcode</span>
                  <span className="font-mono text-red-700 font-black tracking-wider select-all">
                    {item.barcode}
                  </span>
                </div>
                <div className="flex items-center justify-between text-xs px-1">
                  <span className="text-slate-400 font-bold uppercase text-[10px]">RFID</span>
                  <span className="font-mono text-slate-600 font-bold truncate max-w-[150px] select-all">
                     {item.rfid || item.metadata?.RFID || 'No registrado'}
                  </span>
                </div>
              </div>

              {/* 4. ACCIONES DINÁMICAS */}
              <div className="grid grid-cols-2 gap-3 pt-4 border-t border-red-100/50">
                {/* Acción 1: Retirada o Cancelar */}
                {!item.isWithdrawn ? (
                  <button
                    onClick={() => handleAction(item.barcode, 'withdrawn')}
                    className="flex items-center justify-center gap-2 bg-white border-2 border-red-200 hover:bg-red-600 hover:text-white hover:border-red-600 text-red-700 py-3 rounded-2xl text-xs font-black transition-all active:scale-95 shadow-sm uppercase tracking-tight"
                  >
                    <SafeIcon icon={FiPackage} />
                    Retirada
                  </button>
                ) : (
                  <button
                    onClick={() => handleAction(item.barcode, 'undo_withdrawn')}
                    className="flex items-center justify-center gap-2 bg-white border-2 border-orange-300 hover:bg-orange-50 text-orange-700 py-3 rounded-2xl text-xs font-black transition-all active:scale-95 shadow-sm uppercase tracking-tight"
                  >
                    <SafeIcon icon={FiRotateCcw} />
                    Cancelar
                  </button>
                )}

                {/* Acción 2: Enviada (Solo Manager) */}
                {isManager ? (
                  <button
                    onClick={() => handleAction(item.barcode, 'sent')}
                    className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-3 rounded-2xl text-xs font-black transition-all active:scale-95 shadow-lg shadow-red-200 uppercase tracking-tight"
                  >
                    <SafeIcon icon={FiSend} />
                    Enviada
                  </button>
                ) : (
                  <div className="flex items-center justify-center text-[9px] text-slate-400 font-bold italic bg-slate-100/50 rounded-2xl border border-dashed border-slate-200 px-3 text-center leading-tight">
                    Pendiente validación Manager
                  </div>
                )}
              </div>
              
              {/* Badge de Estado para Auditoría */}
              {item.isWithdrawn && (
                <div className="absolute top-0 right-0 bg-orange-600 text-white text-[9px] font-black px-3 py-1.5 rounded-bl-2xl shadow-sm uppercase tracking-widest">
                  Lista para Envío
                </div>
              )}
            </motion.div>
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default UnlinkedGarmentsList;